/* istanbul ignore file */
import {
  PaymentService,
  PaymentStatus,
  SerializableMap,
  TransactionAuthorizationResult,
} from '@arc/unified-payments-frontend/shared-payment';
import { createEffect } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { catchError, exhaustMap, filter, map, takeWhile } from 'rxjs/operators';
import { interval, of } from 'rxjs';
import { AppConfigSettings } from '@arc/arc-shared-frontend';
import { HttpErrorResponse } from '@angular/common/http';
import {
  selectIsPaymentTimeout,
  selectPaymentStatusCheckTimedOut,
} from '../btpay';
import {
  selectPaymentStatusPollingCanceledTransactionResults,
  selectPaymentStatusPollingIntervalData,
} from '../shared-selectors';
import { concatLatestFrom } from '@ngrx/operators';

export abstract class PaymentStatusPollingBaseEffects {
  private checkPaymentStatusTimeout: number;
  private checkPaymentStatusInterval: number;

  constructor(
    protected store: Store,
    protected paymentService: PaymentService,
    protected appConfig: AppConfigSettings,
  ) {
    this.checkPaymentStatusTimeout = +this.appConfig.getconfig(
      'checkPaymentStatusTimeout',
    );

    this.checkPaymentStatusInterval = +this.appConfig.getconfig(
      'checkPaymentStatusInterval',
    );
  }

  pollPaymentStatus$ = createEffect(() =>
    this.store.select(selectIsPaymentTimeout).pipe(
      filter((isTimeout) => isTimeout),
      exhaustMap(() =>
        interval(this.checkPaymentStatusInterval).pipe(
          concatLatestFrom(() => [
            this.store.select(selectPaymentStatusPollingIntervalData),
          ]),
          takeWhile(
            ([, pollingData]) =>
              pollingData.paymentAuthorizationPending &&
              pollingData.statusCheckCount < this.checkPaymentStatusTimeout,
          ),
          exhaustMap(([, pollingData]) =>
            this.paymentService
              .onCheckStatus(
                pollingData.tenantId,
                pollingData.paymentReferenceNumber,
                pollingData.isScheduledTransaction,
              )
              .pipe(
                map((results) => {
                  const authorizationPending = Object.keys(results).some(
                    (key) =>
                      results[key].gpalStatus === PaymentStatus.IN_PROGRESS,
                  );
                  const checkStatusCount =
                    pollingData.statusCheckCount +
                    this.checkPaymentStatusInterval;
                  const checkStatusTimeout =
                    checkStatusCount > this.checkPaymentStatusTimeout &&
                    authorizationPending;

                  return this.onCheckStatusSuccess(
                    results,
                    checkStatusCount,
                    checkStatusTimeout,
                    authorizationPending,
                  );
                }),
                catchError((error: HttpErrorResponse) =>
                  of(this.onCheckStatusError(error)),
                ),
              ),
          ),
        ),
      ),
    ),
  );

  cancelPaymentStatusPolling$ = createEffect(() =>
    this.store.select(selectPaymentStatusCheckTimedOut).pipe(
      filter((isTimedOut) => isTimedOut),
      concatLatestFrom(() => [
        this.store.select(selectPaymentStatusPollingCanceledTransactionResults),
      ]),
      map(([, results]) => this.onCheckStatusCanceled(results)),
    ),
  );

  abstract onCheckStatusSuccess(
    results: SerializableMap<TransactionAuthorizationResult>,
    checkStatusCount: number,
    checkStatusTimeout: boolean,
    authorizationPending: boolean,
  ): Action<string>;

  abstract onCheckStatusError(error: HttpErrorResponse): Action<string>;

  abstract onCheckStatusCanceled(
    results: SerializableMap<TransactionAuthorizationResult>,
  ): Action<string>;
}
